import { useState, useRef, useEffect, type ChangeEvent, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { uploadReceipt, getReceipt } from "../api/documents";
import { ErrorMessage } from "./ErrorMessage";
import { ReceiptResults } from "./ReceiptResults";
import type { Receipt } from "../types/receipt";

interface ReceiptCaptureProps {
  clientId: number;
  onUploadComplete?: () => void;
}

type CaptureStage = "idle" | "uploading" | "fetching" | "results" | "error";

const PROCESSING_MESSAGES = [
  "Uploading your receipt...",
  "Reading the receipt...",
  "Extracting line items...",
  "Matching to your bank charges...",
];

export function ReceiptCapture({ clientId, onUploadComplete }: ReceiptCaptureProps): ReactNode {
  const [stage, setStage] = useState<CaptureStage>("idle");
  const [error, setError] = useState("");
  const [processingStep, setProcessingStep] = useState(0);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (stage !== "uploading" && stage !== "fetching") {
      setProcessingStep(0);
      return;
    }
    if (processingStep >= PROCESSING_MESSAGES.length - 1) return;

    const timer = setTimeout(() => {
      setProcessingStep((prev) => Math.min(prev + 1, PROCESSING_MESSAGES.length - 1));
    }, 1500);
    return () => clearTimeout(timer);
  }, [stage, processingStep]);

  function handleTap(): void {
    fileInputRef.current?.click();
  }

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/heic", "image/heif", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      setError("Please select a photo or PDF file.");
      setStage("error");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("File must be under 10 MB.");
      setStage("error");
      return;
    }

    setStage("uploading");
    setError("");
    setProcessingStep(0);

    try {
      const uploadResult = await uploadReceipt(clientId, file);
      setStage("fetching");
      const doc = await getReceipt(clientId, uploadResult.id);
      setReceipt(doc);
      setStage("results");
      onUploadComplete?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed. Please try again.");
      setStage("error");
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  function handleDismiss(): void {
    setStage("idle");
    setReceipt(null);
  }

  if (stage === "results" && receipt?.extraction) {
    return (
      <ReceiptResults
        extraction={receipt.extraction}
        match={receipt.match}
        onDismiss={handleDismiss}
      />
    );
  }

  return (
    <div className="receipt-capture">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf"
        capture="environment"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      <motion.button
        className="receipt-capture__button"
        onClick={handleTap}
        disabled={stage === "uploading" || stage === "fetching"}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        style={{
          width: "100%",
          padding: "20px",
          border: "2px dashed var(--color-border, #d1d5db)",
          borderRadius: "12px",
          background: "var(--color-surface, #f9fafb)",
          cursor: stage === "uploading" || stage === "fetching" ? "wait" : "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "8px",
          fontSize: "1rem",
        }}
      >
        <AnimatePresence mode="wait">
          {stage === "idle" && (
            <motion.div
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ textAlign: "center" }}
            >
              <div style={{ fontSize: "2rem" }}>📸</div>
              <div style={{ fontWeight: 600 }}>Snap a receipt</div>
              <div
                style={{
                  fontSize: "0.85rem",
                  color: "var(--color-text-muted, #6b7280)",
                }}
              >
                Take a photo or pick a file — we'll read it and match it to your bank charges
              </div>
            </motion.div>
          )}

          {(stage === "uploading" || stage === "fetching") && (
            <motion.div
              key="uploading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ textAlign: "center" }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                style={{ fontSize: "2rem" }}
              >
                ⏳
              </motion.div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={processingStep}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.3 }}
                  style={{ fontWeight: 500 }}
                >
                  {PROCESSING_MESSAGES[processingStep]}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          )}

          {stage === "results" && !receipt?.extraction && (
            <motion.div
              key="no-extraction"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ textAlign: "center" }}
            >
              <div style={{ fontSize: "2rem" }}>📄</div>
              <div style={{ fontWeight: 600 }}>Receipt uploaded</div>
              <div
                style={{
                  fontSize: "0.85rem",
                  color: "var(--color-text-muted, #6b7280)",
                }}
              >
                Could not read this receipt — try a clearer photo
              </div>
            </motion.div>
          )}

          {stage === "error" && (
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ textAlign: "center" }}
            >
              <div style={{ fontSize: "2rem" }}>📸</div>
              <div style={{ fontWeight: 600 }}>Snap a receipt</div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {error && (
        <ErrorMessage
          message={error}
          onDismiss={() => {
            setError("");
            setStage("idle");
          }}
        />
      )}
    </div>
  );
}
